 

import { api } from './index';

import type { MilestoneStep, MilestoneSteps } from '@/types';

export interface GetMilestoneStepsParams {
  complete?: boolean;
  growthPlanId?: string;
  page?: number;
}

export interface GetMilestoneStepParams {
  id: string;
}

export interface CreateMilestoneStepParams extends Partial<MilestoneStep> {}

export interface UpdateMilestoneStepParams extends Partial<MilestoneStep> {
  isComplete?: boolean;
}

export interface DeleteMilestoneStepParams {
  id: string;
}

export interface RefreshMilestoneSuggestedStepParams {
  index: number;
  milestoneId: string;
}

export interface ReorderMilestoneStepParams {
  destination: string;
  origin: string;
}

export const milestoneStepApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get milestone steps with completion status
    getMilestoneSteps: builder.query<MilestoneSteps, GetMilestoneStepsParams>({
      providesTags: ['MilestoneStep'],
      query: (params) => ({
        method: 'GET',
        url: '/steps',
        params,
      }),
    }),

    // Get a single milestone step
    getMilestoneStep: builder.query<MilestoneStep, GetMilestoneStepParams>({
      providesTags: (_, __, { id }) => [{ type: 'MilestoneStep', id }],
      query: ({ id }) => ({
        method: 'GET',
        url: `/steps/${id}`,
      }),
    }),

    // Create a new milestone step
    createMilestoneStep: builder.mutation<
      MilestoneStep,
      CreateMilestoneStepParams
    >({
      invalidatesTags: ['MilestoneStep', 'Milestone'],
      query: (milestoneStep) => ({
        method: 'POST',
        url: '/steps',
        data: milestoneStep,
      }),
    }),

    // Update an existing milestone step
    updateMilestoneStep: builder.mutation<
      MilestoneStep,
      UpdateMilestoneStepParams
    >({
      invalidatesTags: ['MilestoneStep', 'Milestone'],
      query: ({ id, ...milestoneStep }) => ({
        method: 'PATCH',
        url: `/steps/${id}`,
        data: { step: milestoneStep },
      }),
    }),

    // Delete a milestone step
    deleteMilestoneStep: builder.mutation<void, DeleteMilestoneStepParams>({
      invalidatesTags: ['MilestoneStep', 'Milestone'],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/steps/${id}`,
      }),
    }),

    // Reorder milestone steps
    reorderMilestoneSteps: builder.mutation<void, ReorderMilestoneStepParams>({
      invalidatesTags: ['MilestoneStep', 'Milestone'],
      query: (params) => ({
        method: 'PATCH',
        url: '/steps/reorder',
        data: params,
      }),
    }),

    // Refresh milestone suggested step
    refreshMilestoneSuggestedStep: builder.mutation<
      Array<string>,
      RefreshMilestoneSuggestedStepParams
    >({
      invalidatesTags: ['Milestone'],
      query: ({ milestoneId, index }) => ({
        method: 'PATCH',
        url: `/milestones/${milestoneId}/refresh_step`,
        data: { milestone: { index } },
      }),
    }),
  }),
});

export const {
  useGetMilestoneStepsQuery,
  useGetMilestoneStepQuery,
  useCreateMilestoneStepMutation,
  useUpdateMilestoneStepMutation,
  useDeleteMilestoneStepMutation,
  useReorderMilestoneStepsMutation,
  useRefreshMilestoneSuggestedStepMutation,
} = milestoneStepApi;
